import { getGalleryBaseUrl } from '@renderer/utils/brandUtils'
import { useConfigStore } from '@renderer/stores/configState'
import type { ResultPageSettings } from '@renderer/types/configState'

/** Normalize superadmin-configured base to origin only (http/https). */
export function normalizeGalleryPublicBaseUrl(
  input: string | null | undefined
): string | null {
  if (input == null || typeof input !== 'string') return null
  const t = input.trim()
  if (!t) return null
  try {
    const u = new URL(t)
    if (u.protocol !== 'http:' && u.protocol !== 'https:') return null
    return u.origin
  } catch {
    return null
  }
}

export function resolveGallerySessionUrl(
  sessionId: string | undefined | null,
  resultSettings?: Pick<ResultPageSettings, 'galleryPublicBaseUrl'> | null
): string {
  const custom = normalizeGalleryPublicBaseUrl(resultSettings?.galleryPublicBaseUrl ?? null)
  const base = (custom ?? getGalleryBaseUrl()).replace(/\/+$/, '')
  const sid = sessionId != null ? String(sessionId).trim() : ''
  if (!sid) return base
  return `${base}/${sid}`
}

export function resolveGallerySessionUrlFromStore(sessionId: string | undefined | null): string {
  const settings = useConfigStore.getState().config?.pages?.result?.settings
  return resolveGallerySessionUrl(sessionId, settings ?? undefined)
}

/**
 * Framed-strip upload returns both a CDN URL and a local blob URL. Prefer the local URL for
 * in-app `<video src>` so COEP/isolated contexts do not block cross-origin media; keep the
 * remote URL when both exist for sharing/sync (e.g. `videoRemoteUrl` on CapturedItem).
 */
export function resolveFramedVideoPlaybackAndRemote(
  uploadedUrl: string | undefined | null,
  localUrl: string | undefined | null
): { playbackUrl: string; videoRemoteUrl: string | undefined } {
  const remote = typeof uploadedUrl === 'string' ? uploadedUrl.trim() : ''
  const local = typeof localUrl === 'string' ? localUrl.trim() : ''
  const playbackUrl = local || remote
  const videoRemoteUrl =
    remote.length > 0 && local.length > 0 && /^https?:\/\//i.test(remote) ? remote : undefined
  return { playbackUrl, videoRemoteUrl }
}

/**
 * Fetch a remote image and return a blob: URL for use in `<img>` / canvas. The booth uses
 * COEP `require-corp`, which blocks cross-origin embeds unless the asset response sends CORP;
 * public upload URLs often do not. Blob URLs are same-origin to the document so they load.
 * With `maxEdgePx`, decodes the image and scales so the longest side is at most that many
 * pixels (JPEG) to save memory and bandwidth on the booth.
 * Caller must `URL.revokeObjectURL` when the URL is no longer needed.
 */
type RemoteImageBytesResult = {
  ok: boolean
  status?: number
  contentType?: string
  data?: ArrayBuffer
  error?: string
}
type FetchBytesViaMain = (payload: {
  url: string
  timeoutMs?: number
}) => Promise<RemoteImageBytesResult>

/**
 * Last-resort fetch path for http(s) assets whose host doesn't return CORS headers for
 * the booth origin. The renderer's `fetch()` runs under `webSecurity: true` and would
 * otherwise throw a network error; routing the request through the Electron main
 * process bypasses CORS entirely because Node's fetch has no Same-Origin notion.
 * Returns `null` when the preload bridge is unavailable (browser / Tauri), so callers
 * can keep the legacy behavior in those environments.
 */
async function fetchHttpBlobViaMain(url: string): Promise<Blob | null> {
  if (typeof window === 'undefined') return null
  const bridge = window as typeof window & {
    api?: { fetchRemoteImageBytes?: FetchBytesViaMain }
  }
  const fn = bridge.api?.fetchRemoteImageBytes
  if (typeof fn !== 'function') return null
  if (!url.startsWith('http://') && !url.startsWith('https://')) return null
  const res = await fn({ url })
  if (!res.ok || !res.data) return null
  const type = res.contentType || 'application/octet-stream'
  return new Blob([res.data], { type })
}

export async function fetchRemoteImageAsBlobUrl(
  url: string,
  options?: { maxEdgePx?: number }
): Promise<string> {
  let blob: Blob | null = null
  try {
    const res = await fetch(url, { mode: 'cors', credentials: 'omit', cache: 'force-cache' })
    if (res.ok) {
      blob = await res.blob()
    }
  } catch {
    // Swallow — most common cause is a missing CORS header on the asset host.
    // Fall through to the main-process fetch below before giving up.
  }
  if (!blob) {
    blob = await fetchHttpBlobViaMain(url)
  }
  if (!blob) {
    throw new Error('fetch failed (cors + main-process bridge both unavailable)')
  }
  const maxEdge = options?.maxEdgePx
  if (maxEdge == null || maxEdge <= 0) {
    return URL.createObjectURL(blob)
  }

  let bitmap: ImageBitmap | undefined
  try {
    bitmap = await createImageBitmap(blob)
    const { width, height } = bitmap
    const longEdge = Math.max(width, height)
    const scale = Math.min(1, maxEdge / longEdge)
    if (scale >= 1) {
      bitmap.close()
      return URL.createObjectURL(blob)
    }
    const w = Math.max(1, Math.round(width * scale))
    const h = Math.max(1, Math.round(height * scale))
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) {
      bitmap.close()
      return URL.createObjectURL(blob)
    }
    ctx.drawImage(bitmap, 0, 0, w, h)
    bitmap.close()
    bitmap = undefined

    const jpegBlob = await new Promise<Blob>((resolve, reject) => {
      canvas.toBlob(
        (b) => {
          if (b) resolve(b)
          else reject(new Error('toBlob failed'))
        },
        'image/jpeg',
        0.88
      )
    })
    return URL.createObjectURL(jpegBlob)
  } catch {
    if (bitmap) bitmap.close()
    return URL.createObjectURL(blob)
  }
}

/**
 * Build two JPEG blob URLs for a before/after compare UI: same canvas size and a single shared
 * scale so layers align when stacked (avoids independent `object-fit` / per-image downscale drift).
 * Caller must revoke both URLs when done.
 */
export async function createAlignedComparePairBlobUrls(
  beforeBlobUrl: string,
  afterBlobUrl: string,
  options: { aspectW: number; aspectH: number; maxEdgePx: number }
): Promise<{ before: string; after: string }> {
  const { aspectW, aspectH, maxEdgePx } = options
  if (aspectW <= 0 || aspectH <= 0 || maxEdgePx <= 0) {
    throw new Error('invalid aspect or maxEdgePx')
  }

  const loadBitmap = async (blobUrl: string): Promise<ImageBitmap> => {
    const res = await fetch(blobUrl)
    if (!res.ok) throw new Error(`fetch blob url failed ${res.status}`)
    const blob = await res.blob()
    return createImageBitmap(blob)
  }

  const downscaleUniform = async (bitmap: ImageBitmap, tw: number, th: number): Promise<ImageBitmap> => {
    const canvas = document.createElement('canvas')
    canvas.width = tw
    canvas.height = th
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('no 2d context')
    ctx.drawImage(bitmap, 0, 0, tw, th)
    const out = await createImageBitmap(canvas)
    bitmap.close()
    return out
  }

  let b1: ImageBitmap | undefined
  let b2: ImageBitmap | undefined
  try {
    b1 = await loadBitmap(beforeBlobUrl)
    try {
      b2 = await loadBitmap(afterBlobUrl)
    } catch (e) {
      b1.close()
      b1 = undefined
      throw e
    }

    const decodeCap = Math.max(maxEdgePx * 2, 2048)
    const maxSide = Math.max(b1.width, b1.height, b2.width, b2.height, 1)
    const decodeScale = Math.min(1, decodeCap / maxSide)
    if (decodeScale < 1) {
      const rw1 = Math.max(1, Math.round(b1.width * decodeScale))
      const rh1 = Math.max(1, Math.round(b1.height * decodeScale))
      const rw2 = Math.max(1, Math.round(b2.width * decodeScale))
      const rh2 = Math.max(1, Math.round(b2.height * decodeScale))
      b1 = await downscaleUniform(b1, rw1, rh1)
      b2 = await downscaleUniform(b2, rw2, rh2)
    }

    const w1 = b1.width
    const h1 = b1.height
    const w2 = b2.width
    const h2 = b2.height
    if (!w1 || !h1 || !w2 || !h2) throw new Error('invalid bitmap dimensions')

    const portrait = aspectH >= aspectW
    let canvasW: number
    let canvasH: number
    if (portrait) {
      canvasH = maxEdgePx
      canvasW = Math.max(1, Math.round((maxEdgePx * aspectW) / aspectH))
    } else {
      canvasW = maxEdgePx
      canvasH = Math.max(1, Math.round((maxEdgePx * aspectH) / aspectW))
    }

    const s = Math.min(canvasW / w1, canvasH / h1, canvasW / w2, canvasH / h2)

    const renderJpegBlobUrl = (bitmap: ImageBitmap, w: number, h: number): Promise<string> => {
      const canvas = document.createElement('canvas')
      canvas.width = canvasW
      canvas.height = canvasH
      const ctx = canvas.getContext('2d')
      if (!ctx) throw new Error('no 2d context')
      ctx.fillStyle = '#ffffff'
      ctx.fillRect(0, 0, canvasW, canvasH)
      const dw = w * s
      const dh = h * s
      const x = (canvasW - dw) / 2
      const y = (canvasH - dh) / 2
      ctx.drawImage(bitmap, x, y, dw, dh)
      return new Promise((resolve, reject) => {
        canvas.toBlob(
          (blob) => {
            if (blob) resolve(URL.createObjectURL(blob))
            else reject(new Error('toBlob failed'))
          },
          'image/jpeg',
          0.92
        )
      })
    }

    const [outBefore, outAfter] = await Promise.all([
      renderJpegBlobUrl(b1, w1, h1),
      renderJpegBlobUrl(b2, w2, h2),
    ])

    return { before: outBefore, after: outAfter }
  } finally {
    b1?.close()
    b2?.close()
  }
}
